import { moduleNames } from '@va/constants';
import { isContentAnalysisModuleEnabled } from '@va/dashboard/selectors/core';
import { featureFlagOverrideStorageKeys, FlagsmithFeatureFlags, LocalStorage } from '@va/util/helpers';
import { useIsFeatureEnabled } from '@va/util/hooks';
import { SsrOnboarding } from '@va/wix/shared/components';
import { ComponentType, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

export type LazyLoadedComponent = { default: ComponentType<any> };

const Onboarding = lazy(() => import('wix/components/pages/onboarding/Onboarding'));
const Layout = lazy(() => import('wix/components/layout/Layout'));
const Visitors = lazy(() => import('@va/dashboard/tabs/visitors'));
const PagesTab = lazy(() => import('@va/dashboard/tabs/pages'));

const CampaignsTab = lazy(() => import('@va/dashboard/tabs/campaigns'));

const GenerateImpersonate = lazy(() => import('wix/components/pages/GenerateImpersonate'));
const ViewImpersonate = lazy(() => import('wix/components/pages/ViewImpersonate'));

const OverviewTab = lazy(() => import('@va/dashboard/tabs/overview'));
const RedirectToOverview = lazy(() => import('@va/dashboard/tabs/overview/redirect'));

const GeneralSettingsTab = lazy(() => import('wix/components/tabs/general-settings/GeneralSettingsTab'));
const ConversionsSettingsTab = lazy(() => import('@va/dashboard/tabs/conversions-settings'));
const MetaDataAnalyticsTab = lazy(() =>
  import('@va/dashboard/feature/metadata-analytics').then((module): LazyLoadedComponent => ({
    default: module.MetaDataAnalyticsPage,
  })),
);
const TrendIntelTab = lazy(() =>
  import('@va/dashboard/feature/trend-intel').then((module): LazyLoadedComponent => ({
    default: module.TrendIntelPage,
  })),
);
const ManageApiKeysTab = lazy(() => import('@va/dashboard/tabs/manage-api-keys'));
const ModulesSettingsPage = lazy(() => import('@va/dashboard/tabs/module-settings'));
const PageSettingsTab = lazy(() => import('@va/dashboard/tabs/page-settings'));
const PrivacyCenterTab = lazy(() => import('@va/dashboard/tabs/privacy-center'));
const VisitorSegments = lazy(() => import('@va/dashboard/feature/visitor-segments'));
const ExportTab = lazy(async () => (await import('@va/dashboard/tabs/export')) as LazyLoadedComponent);

const UnsupportedLocalStorage = lazy(
  async () => (await import('@va/dashboard/tabs/unsupported-local-storage')) as LazyLoadedComponent,
);

const IgnoreVisitsPage = lazy(async () => (await import('wix/components/pages/IgnoreVisits')) as LazyLoadedComponent);
const NotFoundWebsitePage = lazy(() => import('wix/components/pages/NotFoundWebsitePage'));
const GeneralErrorPage = lazy(() => import('wix/components/pages/WixGeneralError'));
const UnsubscribePage = lazy(() => import('@va/dashboard/tabs/unsubscribe'));
const UpgradePlanPage = lazy(() => import('wix/components/pages/UpgradePlan'));
const PricingPage = lazy(() => import('wix/components/pages/Pricing'));

const VisitorsHistoryV2 = lazy(() => import('@va/dashboard/tabs/visitors/history'));

const TrafficDetailsPage = lazy(() => import('@va/dashboard/tabs/traffic'));
const OutgoingTrafficTab = lazy(
  async () => (await import('@va/dashboard/tabs/outgoing-traffic')) as LazyLoadedComponent,
);

const ConversionEventsTab = lazy(async () => (await import('@va/dashboard/tabs/conversions')) as LazyLoadedComponent);
const DevicesTab = lazy(() => import('@va/dashboard/tabs/devices'));
const CompetitionTab = lazy(() => import('@va/dashboard/tabs/competition'));
const WebsiteErrorsTab = lazy(() => import('@va/dashboard/tabs/website-errors/main'));
const MyCampaignDetails = lazy(() => import('@va/dashboard/tabs/campaigns/details'));

const VisitorRecordingsTabV2 = lazy(() => import('@va/dashboard/tabs/recordings'));
const VisitorRecordingsSettingsOnboarding = lazy(() => import('@va/dashboard/tabs/recordings/onboardings'));

/** EVENT TRACKING */
const EventTrackingModuleTab = lazy(() => import('@va/dashboard/tabs/event-tracking/module'));
const ABEDetailsTab = lazy(() =>
  import('@va/dashboard/modules/event-tracking/alarming-behaviour').then((module) => ({
    default: module.ABEDetailsTab,
  })),
);

/** HEATMAPS */
const HeatmapDetailView = lazy(() => import('@va/dashboard/tabs/heatmaps/details'));
const HeatmapsTab = lazy(() => import('@va/dashboard/tabs/heatmaps'));
const CreateHeatmapPage = lazy(() => import('@va/dashboard/tabs/heatmaps/create'));
/** HEATMAPS */

/**** FUNNELS ****/
const CreateFunnelPage = lazy(() => import('@va/dashboard/tabs/funnels/create'));
const EditFunnelPage = lazy(() => import('@va/dashboard/tabs/funnels/edit'));
const FunnelsTab = lazy(() => import('@va/dashboard/tabs/funnels/main'));
const FunnelDetailsPage = lazy(() => import('@va/dashboard/tabs/funnels/details'));
/**** FUNNELS ****/

const RecordingsDetailsPageV2 = lazy(() => import('@va/dashboard/tabs/recordings/details'));

/**** POLLS ****/
const PollsTab = lazy(() => import('@va/dashboard/tabs/feedback/polls/main'));
const CreatePollPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/create'));
const EditPollPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/edit'));
const PollDetailsPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/details'));
const PollParticipantsResponsesPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/participants'));
/**** POLLS ****/

/**** SURVEYS ****/
const SurveysTab = lazy(() => import('@va/dashboard/tabs/feedback/surveys/main'));
const CreateSurveyPage = lazy(() => import('@va/dashboard/tabs/feedback/surveys/create'));
const EditSurveyPage = lazy(() => import('@va/dashboard/tabs/feedback/surveys/edit'));
const SurveyDetailsPage = lazy(() => import('@va/dashboard/tabs/feedback/surveys/details'));
/**** SURVEYS ****/
const MyAccount = lazy(() => import('wix/components/pages/AccountSettings/MyAccount'));

const WebsiteRoute = ({ component: Component, hasBackButton, extraProps, ...rest }: any) => {
  return (
    <Route
      {...rest}
      render={(props) => (
        <Layout hasBackButton={hasBackButton}>
          <Suspense fallback={<div />}>
            {/* TODO Add wixModals */}
            <Component {...props} {...extraProps} />
          </Suspense>
        </Layout>
      )}
    />
  );
};

const AppRoutes = () => {
  const isMetaDataAnalyticsEnabled = useSelector(isContentAnalysisModuleEnabled);
  const trendIntelEnabled =
    useIsFeatureEnabled(FlagsmithFeatureFlags.TrendIntel) ||
    LocalStorage.getItem(featureFlagOverrideStorageKeys.trendIntelDebug) === 'true';

  return (
    <Suspense fallback={<div />}>
      {/* TODO Add modals here so that they're not rerendered on every route??? */}
      <Switch>
        {/* Admin routes */}
        <Route path='/generate-impersonate' component={GenerateImpersonate} />
        <Route
          path='/impersonate'
          render={(props) => (
            <ViewImpersonate
              impersonateData={
                props.location.state &&
                (props.location.state as { impersonateData: Record<string, unknown> }).impersonateData
              }
            />
          )}
        />
        <Route path='/onboarding'>
          <Onboarding />
        </Route>

        <Route path='/unsupported-localstorage' component={UnsupportedLocalStorage} />
        <Route path='/unsubscribe' render={() => <UnsubscribePage />} />
        <Route exact path='/upgrade' component={UpgradePlanPage} />
        <Route exact path='/pricing' component={PricingPage} />

        <Route path='/visit-settings' component={IgnoreVisitsPage} />
        <Route path='/ignore-visits/:hash' component={IgnoreVisitsPage} />

        <WebsiteRoute exact path='/' component={RedirectToOverview} />
        <WebsiteRoute path='/dashboard' component={OverviewTab} />

        <WebsiteRoute
          exact
          path='/visitors/history/:sessionKey'
          component={VisitorsHistoryV2}
          extraProps={{ isWix: true }}
        />
        <WebsiteRoute path='/visitors' component={Visitors} />

        <WebsiteRoute path='/pages' component={PagesTab} />

        <WebsiteRoute path='/traffic' component={TrafficDetailsPage} />
        <WebsiteRoute path='/outgoing-traffic' component={OutgoingTrafficTab} />
        <WebsiteRoute
          path='/conversion-events'
          component={ConversionEventsTab}
          moduleName={moduleNames.conversionEvents}
        />
        <WebsiteRoute exact path='/devices' component={DevicesTab} />

        <WebsiteRoute exact path='/competition' component={CompetitionTab} />

        <WebsiteRoute
          exact
          // campaignKey is a MD5 value (32 hexadecimal digits)
          path='/campaigns/:campaignKey([A-Fa-f0-9]{32})'
          component={MyCampaignDetails}
        />

        <WebsiteRoute path='/campaigns' component={CampaignsTab} />

        <WebsiteRoute exact path='/settings' component={GeneralSettingsTab} />
        {isMetaDataAnalyticsEnabled && <WebsiteRoute path='/content-analysis' component={MetaDataAnalyticsTab} />}
        {trendIntelEnabled && <WebsiteRoute path='/growth-engine' component={TrendIntelTab} />}
        {/* Keeps old bookmarks and links working after the module was renamed */}
        {trendIntelEnabled && (
          <Route
            path='/trend-intel'
            render={({ location }) => (
              <Redirect to={{ ...location, pathname: location.pathname.replace('/trend-intel', '/growth-engine') }} />
            )}
          />
        )}
        <WebsiteRoute exact path='/settings/api-keys' component={ManageApiKeysTab} />
        <WebsiteRoute path='/settings/conversions' component={ConversionsSettingsTab} />
        <WebsiteRoute
          path='/settings/modules'
          component={SsrOnboarding.bind(this, { children: <ModulesSettingsPage /> })}
        />
        <WebsiteRoute exact path='/settings/pages' component={PageSettingsTab} />
        <WebsiteRoute exact path='/settings/tracking-code' component={SsrOnboarding} />
        <WebsiteRoute path='/settings/privacy-center' component={PrivacyCenterTab} />

        <WebsiteRoute path='/settings/visitor-segments' component={VisitorSegments} hasBackButton />

        <WebsiteRoute
          exact
          path='/behaviour/event-tracking/alarming-behavior-events/:trigger(deadClicks|rageClicks|scrolls|mouseMoves|refreshes|uTurns)'
          component={ABEDetailsTab}
          moduleName={moduleNames.eventTracking}
        />

        <WebsiteRoute
          path='/behaviour/event-tracking'
          component={SsrOnboarding.bind(this, { children: <EventTrackingModuleTab /> })}
        />

        <WebsiteRoute
          exact
          path='/behaviour/recordings-onboarding'
          component={SsrOnboarding.bind(this, { children: <VisitorRecordingsSettingsOnboarding /> })}
        />
        <WebsiteRoute
          exact
          path='/behaviour/recordings'
          component={SsrOnboarding.bind(this, { children: <VisitorRecordingsTabV2 /> })}
        />
        <WebsiteRoute exact path='/behaviour/funnels/create' component={CreateFunnelPage} />
        <WebsiteRoute
          exact
          path='/behaviour/funnels/:funnelId([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})'
          component={FunnelDetailsPage}
        />
        <WebsiteRoute exact path='/behaviour/funnels/edit/:funnelId' component={EditFunnelPage} />
        <WebsiteRoute path='/behaviour/funnels' component={FunnelsTab} />

        <WebsiteRoute
          // 24-character hexadecimal string value
          path='/behaviour/heatmaps/:heatmapId([A-Fa-f0-9]{24})'
          component={HeatmapDetailView}
          hasBackButton
        />

        <WebsiteRoute
          path='/behaviour/heatmaps/create'
          component={SsrOnboarding.bind(this, { children: <CreateHeatmapPage /> })}
        />

        <WebsiteRoute path='/behaviour/heatmaps' component={SsrOnboarding.bind(this, { children: <HeatmapsTab /> })} />

        {/* Feedback/Polls */}
        <WebsiteRoute exact path='/feedback/polls' component={SsrOnboarding.bind(this, { children: <PollsTab /> })} />

        <WebsiteRoute
          exact
          path='/feedback/polls/create'
          component={SsrOnboarding.bind(this, { children: <CreatePollPage poll={undefined} /> })}
        />

        <WebsiteRoute exact path='/feedback/polls/:id/edit' component={EditPollPage} />

        <WebsiteRoute
          exact
          path='/feedback/polls/:id/visitors/:visitorKey/responses'
          component={PollParticipantsResponsesPage}
        />

        <WebsiteRoute path='/feedback/polls/:id' component={PollDetailsPage} />

        {/* Feedback/Surveys */}
        <WebsiteRoute exact path='/feedback/surveys' component={SurveysTab} />

        <WebsiteRoute exact path='/feedback/surveys/create' component={CreateSurveyPage} />
        <WebsiteRoute exact path='/feedback/surveys/:id/edit' component={EditSurveyPage} />

        <WebsiteRoute path='/feedback/surveys/:id' component={SurveyDetailsPage} />

        <WebsiteRoute path='/export' component={ExportTab} />

        <WebsiteRoute exact path='/behaviour/recordings/:sessionId' component={RecordingsDetailsPageV2} />
        <WebsiteRoute exact path='/website-errors' component={WebsiteErrorsTab} />

        <WebsiteRoute path='/my-account' component={MyAccount} />

        <Route path='/not-found-website' component={NotFoundWebsitePage} />
        <Route component={GeneralErrorPage} />
      </Switch>
    </Suspense>
  );
};

export const getRoutes = () => {
  return <AppRoutes />;
};
